import { env } from '@repo/env';
import type { Metadata } from 'next';
import type { AlternateURLs } from 'next/dist/lib/metadata/types/alternative-urls-types';
import type { Robots } from 'next/dist/lib/metadata/types/metadata-types';
import type { EntityCountByLocation } from '../../base/metadata/types';
import { ReviewMetadata } from '../../review/base/metadata/types';
import { RoundupMetadata } from '../../roundup/base/metadata/types';
import { SLUGS_WITH_MONETIZABLE_FILTER } from '../constants';
import { getEntityCountByLocation } from '../geo-data';
import { filterByMonetizable } from '../roundup-monetizable-filter';
import { DEFAULT_OG_IMAGE, DEFAULT_OG_IMAGE_HEIGHT, DEFAULT_OG_IMAGE_WIDTH } from './constants';

type MetadataSchema = RoundupMetadata | ReviewMetadata;

export interface MetadataOverrides {
  seo?: Pick<MetadataSchema['seo'], 'nofollow' | 'noindex'>;
  link?: string;
}

const isRoundupMetadata = (metadata: MetadataSchema): metadata is RoundupMetadata =>
  'summaryTableSection' in metadata;

export const getEntityCount = (
  entityCountByLocation: EntityCountByLocation,
  //Leaving geodataState here in case it's useful for https://nerdwallet.atlassian.net/browse/WEALTH-38
  geodataState?: string,
) =>
  (entityCountByLocation.none || 0) + (geodataState ? entityCountByLocation[geodataState] || 0 : 0);

function getRobotsMetadata(
  seo: MetadataSchema['seo'] | NonNullable<MetadataOverrides['seo']>,
): Robots {
  const IS_PRODUCTION = env.NEXT_PUBLIC_DEPLOYABLE_ENV === 'prod';
  const shouldAllowFollow = IS_PRODUCTION && !seo.nofollow;
  const shouldAllowIndex = IS_PRODUCTION && !seo.noindex;

  return {
    follow: shouldAllowFollow,
    index: shouldAllowIndex,
    ...(shouldAllowIndex ? { 'max-snippet': -1, 'max-video-preview': -1 } : {}),
  };
}

function getLanguagesMetadata(hreflang?: MetadataSchema['hreflang']): AlternateURLs['languages'] {
  const languages = Object.fromEntries((hreflang || []).map(item => [item.locale, item.url]));
  return languages;
}

const getSeoTitle = (
  metadata: MetadataSchema,
  regionCode?: string,
  slug?: string,
  isHysaRedesignTreatment?: boolean,
): string => {
  let title = metadata?.seo?.title ?? metadata.title;

  if (isRoundupMetadata(metadata) && metadata.includeEntityCountFromTitle && title) {
    const rows = metadata?.summaryTableSection?.rows ?? [];
    let numberOfEntitiesDisplayed;
    if (slug && SLUGS_WITH_MONETIZABLE_FILTER.includes(slug) && rows) {
      const filteredRows = filterByMonetizable(rows, slug, isHysaRedesignTreatment);
      const entityCountByLocationFiltered = getEntityCountByLocation(filteredRows);
      numberOfEntitiesDisplayed = getEntityCount(entityCountByLocationFiltered, regionCode);
    } else {
      const entityCountByLocationUnfiltered = getEntityCountByLocation(rows);

      numberOfEntitiesDisplayed = getEntityCount(entityCountByLocationUnfiltered, regionCode);
    }

    title = numberOfEntitiesDisplayed > 1 ? `${numberOfEntitiesDisplayed} ${title}` : title;
  }
  return title;
};

export default function convertMetadataToNextMetadata(
  metadata: MetadataSchema,
  metadataOverrides?: MetadataOverrides,
  regionCode?: string,
  slug?: string,
  isHysaRedesignTreatment?: boolean,
): Metadata {
  const languages = getLanguagesMetadata(metadata.hreflang);
  const robots = getRobotsMetadata(metadataOverrides?.seo ?? metadata.seo);
  const canonicalUrl = metadataOverrides?.link ?? metadata.link;
  const defaultLanguage = { 'x-default': canonicalUrl };

  const images = [
    {
      url: metadata.featuredMedia?.sourceUrl ?? DEFAULT_OG_IMAGE,
      secureUrl: metadata.featuredMedia?.sourceUrl ?? DEFAULT_OG_IMAGE,
      height: metadata.featuredMedia?.height ?? DEFAULT_OG_IMAGE_HEIGHT,
      width: metadata.featuredMedia?.width ?? DEFAULT_OG_IMAGE_WIDTH,
    },
  ];

  const twitterTitle = metadata.seo?.twitter?.title || metadata.seo?.title || metadata.title;
  const twitterDescription = metadata.seo?.twitter?.description || metadata.seo?.description;
  const title = getSeoTitle(metadata, regionCode, slug, isHysaRedesignTreatment);

  return {
    // A plain string, not `new URL(...)` — this return value crosses a "use cache" boundary
    // (getRoundupMetadata/getReviewMetadata) and URL instances aren't a supported cache-return
    // type. Next's Metadata type accepts a string here just as well.
    metadataBase: env.NEXT_PUBLIC_NW_URL,
    title: title,
    description: metadata.seo.description,
    alternates: {
      canonical: canonicalUrl,
      languages: metadata.hreflang?.length ? { ...defaultLanguage, ...languages } : defaultLanguage,
    },
    robots: {
      ...robots,
      'max-image-preview': 'large',
    },
    twitter: {
      card: 'summary_large_image',
      title: twitterTitle,
      description: twitterDescription,
      site: '@NerdWallet',
      images,
    },
    openGraph: {
      type: 'article',
      title: title,
      description: metadata.seo.description,
      url: canonicalUrl,
      siteName: 'NerdWallet',
      locale: 'en_US',
      images,
    },
  };
}
